import * as Long from "long";
import { selectBalancingFarmEventById, selectTournamentLeagueMinimumLevel, } from "../balancingSelectors/selectBalancingFarmEvents";
import { selectBalancingOutskirtUnlockLevel } from "../balancingSelectors/selectBalancingOutskirts";
import { IdOutskirtBuildings } from "../constants/IdOutskirtBuildings";
import { IdPersistence } from "../constants/IdPersistence";
import { mapEventTypeToPersistenceKey } from "../constants/mapEventTypeToPersistenceKey";
import { EventType, } from "../features/balancing/types/BalancingCore";
import { IdTournamentCategory } from "../features/tournament-building/IdTournamentCategory";
import { IdTournamentPersistence } from "../features/tournament-building/IdTournamentPersistence";
import { IdTournamentProgression } from "../features/tournament-building/IdTournamentProgression";
import { utilPersistence } from "../utils/utilPersistence";
import { utilTournament } from "../utils/utilTournament";
import { selectActiveCollectEvent } from "./selectCollectEvents";
import { selectCoopInfo } from "./selectCoopInfo";
import { selectEventEndTime, selectEventInfo, selectEventInfosByEventType, selectEventRewardsResources, selectIsEventRunningByEventType, selectIsEventRunningById, selectRunningEventInfosByEventType, } from "./selectEventInfo";
import { selectPlayerLevel } from "./selectPlayerLevelInfo";
export var selectTournamentInfo = function (state) { return state.leagueInfo; };
export var selectTournamentInfoByEventType = function (state, eventType) {
    return selectTournamentInfo(state).leagues.get(eventType);
};
export var selectRunningTournamentByEventType = function (state, eventType) { return selectRunningEventInfosByEventType(state, eventType)[0]; };
export var selectPlayerTournamentBuildingActiveTabId = function (state) {
    if (selectIsRankingAvailable(state)) {
        return IdTournamentCategory.LeagueRanking;
    }
    var eventType = selectSelectedLeague(state);
    if (eventType === EventType.CoopLeague) {
        return IdTournamentCategory.CoopLeague;
    }
    return IdTournamentCategory.PlayerLeague;
};
export var selectTournamentDetailedRankings = function (state) {
    var rankings = [];
    var players = selectTournamentPlayerRankings(state);
    var difficulty = selectTournamentDifficultyInfos(state);
    if (difficulty === undefined || difficulty.rankingReward === undefined) {
        return rankings;
    }
    var max = 1;
    var rankingNumber = 1;
    difficulty.rankingReward.forEach(function (rankingReward) {
        var min = rankingReward.requiredRank;
        var rewards = rankingReward.resourceReward || [];
        var promotionType = rankingReward.promotionType;
        rankings.push({
            max: max,
            min: min,
            players: players.slice(max - 1, min),
            promotion: promotionType,
            rankingNumber: rankingNumber,
            rewards: rewards,
        });
        rankingNumber++;
        max = min + 1;
    });
    return rankings;
};
export var selectTournamentPlayerRankings = function (state) {
    return state.leagueRanking.rankingEntry || [];
};
export var selectIsCollectLeagueAvailable = function (state) {
    var currentTournamentId = selectSelectedLeague(state);
    var currentCollectEvent = selectActiveCollectEvent(state);
    var collectEventId = currentCollectEvent ? currentCollectEvent.typeId : undefined;
    var isCollectTournamentSelected = currentTournamentId === EventType.CollectLeague;
    var isEventRunning = collectEventId != undefined && selectIsEventRunningById(state, collectEventId);
    return isCollectTournamentSelected && isEventRunning;
};
export var selectTournamentEndTime = function (state, currentTournamentInfo) {
    if (currentTournamentInfo != undefined) {
        // Events are sorted by startTime
        return selectEventEndTime(currentTournamentInfo.proto);
    }
    return 0;
};
export var selectTournamentPositionLocas = function (state, eventId) {
    var tournamentPositionId = selectTournamentDifficulty(state, eventId);
    return utilTournament.selectLocasFromIdLeagueDifficulty(tournamentPositionId);
};
export var selectTournamentDifficultyInfos = function (state) {
    var event = selectSelectedLeagueEventData(state);
    var selectedId = selectSelectedLeague(state);
    if (selectedId &&
        event != undefined &&
        event.balancing != undefined &&
        event.balancing.difficultyLevel != undefined) {
        var difficulty = selectTournamentDifficulty(state, event.balancing.type);
        return event.balancing.difficultyLevel[difficulty];
    }
    return;
};
export var selectTournamentIdByEventType = function (state, event) {
    var leagueEvents = state.eventInfo.leagueEvent;
    if (leagueEvents === undefined) {
        return undefined;
    }
    var selectedLeagueEvent = selectRunningTournamentByEventType(state, event);
    return selectedLeagueEvent ? selectedLeagueEvent.proto.eventId : undefined;
};
export var selectIsTournamentLevelMatched = function (state, eventType) {
    var requiredLevel = selectTournamentLeagueMinimumLevel(eventType);
    if (!requiredLevel) {
        return false;
    }
    var playerLevel = state.playerInfo.level || 0;
    return playerLevel >= requiredLevel;
};
export var selectIsTournamentCalloutNeeded = function (state) {
    var _a, _b;
    if (!selectIsPlayerQualifiedForTournament(state)) {
        return false;
    }
    var tournamentId = EventType.PlayerLeague;
    var isPlayerLeagueRunning = selectIsEventRunningByEventType(state, tournamentId);
    if (!isPlayerLeagueRunning) {
        tournamentId = EventType.CoopLeague;
        var isCoopLeagueRunning = selectIsEventRunningByEventType(state, tournamentId);
        if (!isCoopLeagueRunning) {
            return false;
        }
    }
    var cachedEvent = utilPersistence.getValue(IdPersistence.TournamentInfo, (_a = {},
        _a[IdTournamentPersistence.Confirmed] = false,
        _a[IdTournamentPersistence.LastTournament] = 0,
        _a));
    var lastTournament = selectTournamentIdByEventType(state, tournamentId);
    if (!lastTournament ||
        (lastTournament === cachedEvent.LastTournament && cachedEvent.Confirmed)) {
        return false;
    }
    utilPersistence.setValue(IdPersistence.TournamentInfo, (_b = {},
        _b[IdTournamentPersistence.Confirmed] = false,
        _b[IdTournamentPersistence.LastTournament] = lastTournament,
        _b));
    return true;
};
export var selectTournamentRankProgression = function (state, eventId) {
    var key = mapEventTypeToPersistenceKey.get(eventId);
    if (key === undefined) {
        return IdTournamentProgression.NoProgression;
    }
    var previousRank = utilPersistence.getValue(key, Number.MAX_VALUE);
    var currentRank = selectTournamentDifficulty(state, eventId) + 1;
    if (previousRank !== Number.MAX_VALUE && previousRank !== currentRank) {
        if (previousRank < currentRank) {
            return IdTournamentProgression.Promoted;
        }
        return IdTournamentProgression.Demoted;
    }
    return IdTournamentProgression.NoProgression;
};
export var selectRunningTournament = function (state) {
    var runningPlayerLeague = selectRunningTournamentByEventType(state, EventType.PlayerLeague);
    var runningCoopLeague = selectRunningTournamentByEventType(state, EventType.CoopLeague);
    return runningPlayerLeague || runningCoopLeague;
};
export var selectRunningTournamentEventId = function (state) {
    var currentTournament = selectRunningTournament(state);
    return currentTournament && currentTournament.balancing
        ? currentTournament.balancing.type
        : undefined;
};
export var selectRunningTournamentInfo = function (state) {
    var eventType = selectRunningTournamentEventId(state);
    if (eventType) {
        return selectEventInfosByEventType(state, eventType);
    }
    return;
};
export var selectSelectedLeague = function (state) {
    return selectTournamentInfo(state).selectedLeague;
};
export var selectIsRankingAvailable = function (state) {
    var tournamentType = selectSelectedLeague(state);
    var difficulty = selectTournamentDifficulty(state, tournamentType || EventType.PlayerLeague);
    if (tournamentType == undefined) {
        return false;
    }
    var tournamentInfo = selectRunningTournamentByEventType(state, tournamentType);
    if (tournamentInfo == undefined || tournamentInfo.balancing == undefined) {
        return false;
    }
    var tournamentId = tournamentInfo.balancing.id;
    var points = selectLeaguePoints(state);
    var event = selectBalancingFarmEventById(tournamentId);
    if (event &&
        event.difficultyLevel &&
        event.difficultyLevel[difficulty] &&
        event.difficultyLevel[difficulty].minPointsForRanking !== undefined) {
        var pointsToSurpass = event.difficultyLevel[difficulty].minPointsForRanking;
        return points.greaterThanOrEqual(pointsToSurpass);
    }
    return false;
};
export var selectLeaguePoints = function (state) {
    var leagueType = selectSelectedLeague(state);
    if (leagueType == undefined) {
        return Long.ZERO;
    }
    var leagueInfo = selectTournamentInfoByEventType(state, leagueType);
    return leagueInfo !== undefined &&
        leagueInfo.eventProgress !== undefined &&
        leagueInfo.eventProgress.points !== undefined
        ? leagueInfo.eventProgress.points
        : Long.ZERO;
};
export var selectAllLeagueRewardsCollected = function (state) {
    var currentPoints = selectLeaguePoints(state);
    var rewards = selectTournamentRewards(state);
    if (rewards.length <= 0) {
        return true;
    }
    var maxPoints = rewards[rewards.length - 1].requiredThreshold;
    return currentPoints.greaterThanOrEqual(maxPoints);
};
export var selectSelectedLeagueEventData = function (state) {
    var selectedTournament = selectSelectedLeague(state);
    if (selectedTournament == undefined) {
        return undefined;
    }
    if (selectedTournament !== EventType.CollectLeague) {
        return selectEventInfosByEventType(state, selectedTournament);
    }
    var activeCollectEvent = selectActiveCollectEvent(state);
    if (activeCollectEvent == undefined || activeCollectEvent.eventId == undefined) {
        return;
    }
    return selectEventInfo(state, activeCollectEvent.eventId);
};
export var selectTournamentRewards = function (state) {
    var eventInfo = selectSelectedLeagueEventData(state);
    if (eventInfo == undefined) {
        return [];
    }
    var currentDifficulty = selectTournamentDifficultyInfos(state);
    if (currentDifficulty == undefined) {
        return [];
    }
    var rewardId = eventInfo.proto.rewardId;
    return selectEventRewardsResources(state, currentDifficulty, rewardId);
};
export var selectTournamentRewardIndex = function (state) {
    var selectedLeague = selectSelectedLeague(state);
    if (selectedLeague == undefined) {
        return 0;
    }
    var rewards = selectTournamentRewards(state);
    var currentPoints = selectLeaguePoints(state);
    var index = rewards.findIndex(function (reward) { return currentPoints.lessThan(reward.requiredThreshold); });
    return index === -1 ? rewards.length - 1 : index;
};
export var selectIsPlayerQualifiedForTournament = function (state) {
    var level = selectPlayerLevel(state);
    return level >= selectBalancingOutskirtUnlockLevel(IdOutskirtBuildings.TournamentTent);
};
export var selectTournamentDifficulty = function (state, eventType) {
    if (eventType === EventType.CoopLeague && !selectCoopInfo(state)) {
        return 0;
    }
    var leagueInfo = state.leagueInfo.leagues.get(eventType);
    return leagueInfo !== undefined && leagueInfo.leagueId !== undefined
        ? leagueInfo.leagueId - 1
        : 0;
};



//////////////////
// WEBPACK FOOTER
// ./src/selectors/selectTournamentInfo.ts
// module id = 105
// module chunks = 3